import { formatDateTime } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import type { UserMfaQuery } from "@/v2-backoffice-client/user/mfa/__types__/user.mfa.query"

type AuditLog = UserMfaQuery["getUserMfa"]["AuditLogs"][number]

export type UserMfaAuditLogListProps = {
  logs: AuditLog[]
}

function formatTimestamp(value: AuditLog["ActivityDateTime"]): string {
  if (!value) return "—"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "—"
  // Keep seconds: audit-log timestamps want second precision (forensics).
  return formatDateTime(date, "HH:mm:ss - dd.MM.yyyy")
}

// Read-only list of the user's recent MFA-related directory-audit rows
// (security-info registration / change / deletion), newest first.
function UserMfaAuditLogList(props: UserMfaAuditLogListProps) {
  const { logs } = props

  if (logs.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No recent MFA activity for this user.
      </p>
    )
  }

  return (
    <ul className="flex flex-col divide-y rounded-md border">
      {logs.map((log) => {
        const isFailure = log.Result === "failure"
        return (
          <li key={log.Id} className="flex flex-col gap-0.5 px-3 py-2 text-sm">
            <div className="flex items-center justify-between gap-3">
              <span className="font-medium">{log.ActivityDisplayName}</span>
              <span className="text-xs text-muted-foreground">
                {formatTimestamp(log.ActivityDateTime)}
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span
                className={cn(
                  "capitalize",
                  isFailure ? "text-destructive" : "text-emerald-600",
                )}
              >
                {log.Result || "—"}
              </span>
              {log.InitiatedBy ? <span>· by {log.InitiatedBy}</span> : null}
              {isFailure && log.ResultReason ? (
                <span>· {log.ResultReason}</span>
              ) : null}
            </div>
          </li>
        )
      })}
    </ul>
  )
}

export default UserMfaAuditLogList
